import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaImage, FaPlus, FaTrash, FaMapLocationDot } from "react-icons/fa6";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OltService } from "../../../../../services/OltService";
import { SelectNas } from "../../../../../components/forms/selects/SelectNas";
import { Header } from "../../../../../components/Header";
import { handleDetectGPS } from "../../../../../utilities/leftjs/handleDetectGps";
import { FiCrosshair, FiLoader } from "react-icons/fi";
import { getLocationDetail } from "../../../../../utilities/leftjs/getLocationDetail";
import { InputDefault } from "../../../../../components/forms/InputDefault";
import { TextAreaDefault } from "../../../../../components/forms/TextAreaDefault";
import { SelectPop } from "../../../../../components/forms/selects/SelectPop";


const CARD_STYLE = {
  background: "rgba(15, 23, 42, 0.5)",
  border: "1px solid rgba(51, 65, 85, 0.3)",
  backdropFilter: "blur(12px)",
};

const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

// Reverse Geocoding Helper
const fetchAddressFromCoords = async (lat, lng) => {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
    const response = await fetch(url, {
      headers: { "Accept": "application/json", "User-Agent": "DanelaApp/1.0" }
    });
    if (!response.ok) return null;
    const data = await response.json();
    return data.display_name || null;
  } catch (error) {
    console.error("Gagal mengambil alamat:", error);
    return null;
  }
};

// --- SUB-COMPONENTS (MAP) ---

const MapMarker = ({ position }) => {
  return position ? <Marker position={[position.lat, position.lng]} icon={DefaultIcon} /> : null;
};

const MapController = ({ data, setData }) => {
  const map = useMap();

  const handleUpdateLocation = async (lat, lng) => {
    setData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    map.flyTo([lat, lng], 17);

    const address = await fetchAddressFromCoords(lat, lng);
    if (address) {
      setData((prev) => ({ ...prev, address: address }));
    }
  };

  useMapEvents({
    click(e) {
      handleUpdateLocation(e.latlng.lat, e.latlng.lng);
    },
  });

  const handleLocate = () => {
    if (!navigator.geolocation) return alert("Browser tidak mendukung Geolocation");
    map.locate({ setView: false, maxZoom: 16 });
    map.once("locationfound", async (e) => {
      handleUpdateLocation(e.latlng.lat, e.latlng.lng);
    });
    map.once("locationerror", () => alert("Gagal mendapatkan lokasi. Pastikan GPS aktif."));
  };

  return (
    <>
      <MapMarker position={{ lat: data.latitude, lng: data.longitude }} />
      <button
        type="button"
        onClick={handleLocate}
        className="absolute bottom-6 right-6 z-[1000] bg-slate-800 text-cyan-400 p-3 rounded-full shadow-2xl border border-slate-600 hover:bg-cyan-600 hover:text-white transition-all active:scale-90 flex items-center justify-center"
        title="Deteksi Lokasi Saya"
      >
        <FaMapLocationDot size={20} />
      </button>
    </>
  );
};

// --- MAIN COMPONENT ---

export const OltCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);

  const defaultCenter = { lat: -6.17511, lng: 106.827153 };

  const [formData, setFormData] = useState({
    pop_id: "",
    nas_id: "",
    main_port: "",
    name: "",
    pon: [],
    description: "",
    longitude: defaultCenter.lng,
    latitude: defaultCenter.lat,
    address: "",
    images: [],
  });

  const [error, setError] = useState({
    nas_id: "",
    main_port: "",
    name: "",
    pon: "",
    description: "",
    longitude: "",
    latitude: "",
    address: "",
    images: "",
  });

  // --- Handlers ---

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // PON Handlers
  const addPon = () => {
    const newPonIndex = formData.pon.length + 1;
    setFormData((prev) => ({
      ...prev,
      pon: [...prev.pon, { name: `PON ${newPonIndex}`, max_user: "" }],
    }));
  };

  const removePon = (index) => {
    setFormData((prev) => ({
      ...prev,
      pon: prev.pon.filter((_, i) => i !== index),
    }));
  };

  const handlePonChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedPon = [...prev.pon];
      updatedPon[index] = { ...updatedPon[index], [field]: value };
      return { ...prev, pon: updatedPon };
    });
  };

  // Image Handler
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 3) return alert("Maksimal 3 gambar.");
    setFormData((prev) => ({ ...prev, images: files }));
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const removeImage = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, images: updatedImages }));
    setImagePreviews(updatedPreviews);
    // Revoke object URL to free memory
    URL.revokeObjectURL(imagePreviews[index]);
  };

  const [loadingGps, setLoadingGps] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const DEFAULT_CENTER = { lat: -6.17511, lng: 106.827153 };
  const [gpsInput, setGpsInput] = useState(`${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`);
  const [gpsError, setGpsError] = useState(null);

  const handleGpsInputChange = (e) => {
    const value = e.target.value;
    setGpsInput(value);

    // Parse string "lat, lng" menjadi angka
    const parts = value.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());

      // Validasi apakah angka yang valid
      if (!isNaN(lat) && !isNaN(lng)) {
        setFormData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
        // Reset error jika user berhasil input manual
        setGpsError(null);
      }
    }
  };

  const handleLocationUpdate = async (lat, lng) => {
    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));

    // 3. Fetch Alamat (Reverse Geocoding)
    setFetchingAddress(true);
    try {
      const locationData = await getLocationDetail(lat, lng);
      if (locationData && locationData.displayName) {
        setFormData((prev) => ({
          ...prev,
          address: locationData.displayName,
        }));
      }
    } catch (err) {
      console.error("Gagal mengambil alamat:", err);
    } finally {
      setFetchingAddress(false);
    }
  };



  // Fungsi untuk deteksi GPS dengan error handling lengkap
  const safeDetectGps = async () => {
    setLoadingGps(true);
    setGpsError(null);

    try {
      await handleDetectGPS({
        setLoading: setLoadingGps,
        handleLocationUpdate: handleLocationUpdate
      });
    } catch (error) {
      console.warn("GPS tidak tersedia atau gagal, menggunakan lokasi default:", error.message || error);

      let errorMessage = "GPS tidak tersedia";
      if (error.code === 1) {
        errorMessage = "Akses lokasi ditolak oleh browser";
      } else if (error.code === 2) {
        errorMessage = "Lokasi tidak tersedia saat ini";
      } else if (error.code === 3) {
        errorMessage = "Waktu habis saat mendeteksi lokasi";
      } else if (error.message?.includes("permission")) {
        errorMessage = "Izin akses lokasi belum diberikan";
      }

      setGpsError(errorMessage);

      setFormData((prev) => ({
        ...prev,
        latitude: DEFAULT_CENTER.lat,
        longitude: DEFAULT_CENTER.lng,
      }));
      // Update input string juga
      setGpsInput(`${DEFAULT_CENTER.lat}, ${DEFAULT_CENTER.lng}`);
    } finally {
      setLoadingGps(false);
    }
  };

  // Deteksi GPS saat komponen mount
  useEffect(() => {
    safeDetectGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Format data sebelum kirim (convert main_port ke number jika ada isinya)
    const payload = {
      ...formData,
      main_port: formData.main_port === "" ? null : parseInt(formData.main_port) || null,
      pon: formData.pon.map((p) => ({
        ...p,
        max_user: p.max_user === "" ? null : parseInt(p.max_user) || null,
      })),
    };

    try {
      await OltService.create({ data: payload });
      navigate("/dashboard/master-data/olt");
    } catch (error) {
      alert("Gagal menyimpan data.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Tambah Data OLT - DANELA NET</title>
        <meta name="description" content="Tambah data OLT baru." />
      </Helmet>

      <div>
        {/* HEADER */}
        <Header showToggleSidebar={false}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-700/50 rounded-full transition-all text-slate-400 hover:text-white"
            >
              <FaChevronLeft />
            </button>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">Tambah Data OLT</h2>
              <p className="text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                Form Input OLT Baru
              </p>
            </div>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* GRID ATAS: INFO & FOTO */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* INFORMASI DASAR */}
              <div className=" rounded-2xl p-5 lg:p-6" style={CARD_STYLE}>
                <h3 className="text-xs font-black uppercase text-slate-400 mb-5 tracking-[0.2em] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                  Informasi Dasar
                </h3>

                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <SelectPop
                      id={"pop_id"}
                      label={"Pilih Pop"}
                      placeholder={"Pilih Pop"}
                      value={formData.pop_id}
                      setValue={(e) => setFormData({ ...formData, pop_id: e })}
                      searchable={true}
                    />


                    <SelectNas
                      id={"nas_id"}
                      label={"Pilih NAS (mikrotik)"}
                      value={formData.nas_id}
                      setValue={(id) => setFormData((prev) => ({ ...prev, nas_id: id }))}
                      isClearable={true}
                      placeholder="Pilih NAS (opsional)"
                    />

                    {/* Nama OLT */}
                    <InputDefault
                      id="name"
                      label="Nama OLT"
                      type="text"
                      placeholder="Cth: OLT A"
                      value={formData.name}
                      onChange={handleChange}
                      error={error.name}
                      required
                    />

                    {/* Port Utama - Boleh Kosong */}
                    <InputDefault
                      id="main_port"
                      label="Port Utama"
                      type="number"
                      placeholder="Kosongkan jika tidak ada"
                      value={formData.main_port}
                      onChange={handleChange}
                      error={error.main_port}
                    />
                  </div>

                  {/* Deskripsi */}
                  <TextAreaDefault
                    id="description"
                    label="Detail OLT"
                    placeholder="Masukkan detail OLT..."
                    value={formData.description}
                    onChange={handleChange}
                    error={error.description}
                    rows={3}
                  />
                </div>
              </div>

              {/* PON LIST */}
              <div className="rounded-2xl p-5 lg:p-6" style={CARD_STYLE}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                    Daftar PON
                    {formData.pon.length > 0 && (
                      <span className="ml-2 text-[10px] font-bold bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded-full">
                        {formData.pon.length} PON
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    onClick={addPon}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-cyan-400 border border-cyan-500/20 bg-cyan-500/5 hover:bg-cyan-500/10 transition-all"
                  >
                    <FaPlus size={10} />
                    Tambah PON
                  </button>
                </div>

                {formData.pon.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mb-4">
                      <FaImage className="text-slate-600" size={24} />
                    </div>
                    <p className="text-sm text-slate-500 font-medium">Belum ada PON ditambahkan</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Klik tombol "Tambah PON" untuk menambahkan
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.pon.map((pon, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 rounded-xl bg-slate-800/30 border border-slate-700/30 hover:border-slate-600/50 transition-all group"
                      >
                        {/* Index Badge */}
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                          <span className="text-xs font-black text-cyan-400">{index + 1}</span>
                        </div>

                        {/* PON Fields */}
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-500 mb-1 uppercase tracking-wider">
                              Nama PON
                            </label>
                            <input
                              type="text"
                              value={pon.name}
                              onChange={(e) => handlePonChange(index, "name", e.target.value)}
                              placeholder={`Cth: PON ${index + 1}`}
                              className="w-full px-3 py-2 text-sm rounded-lg bg-slate-900/50 border border-slate-700/50 text-gray-100 placeholder-slate-600 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-500 mb-1 uppercase tracking-wider">
                              Maksimal User
                            </label>
                            <input
                              type="number"
                              value={pon.max_user}
                              onChange={(e) => handlePonChange(index, "max_user", e.target.value)}
                              placeholder="Cth: 32"
                              min="0"
                              className="w-full px-3 py-2 text-sm rounded-lg bg-slate-900/50 border border-slate-700/50 text-gray-100 placeholder-slate-600 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                            />
                          </div>
                        </div>

                        {/* Delete Button */}
                        <button
                          type="button"
                          onClick={() => removePon(index)}
                          className="flex-shrink-0 mt-5 p-2 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100"
                          title="Hapus PON"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* PETA */}
            <div className="rounded-2xl p-1 overflow-hidden border border-slate-700/50" style={{ background: "rgba(15, 23, 42, 0.5)" }}>
              <div className="p-4 border-b border-slate-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <h3 className="text-xs font-black uppercase text-slate-400 tracking-[0.2em] flex items-center gap-2">
                  <FaMapLocationDot className="text-cyan-500" /> Peta Lokasi
                </h3>
                <div className="flex gap-4 text-xs font-mono">
                  <span className="text-slate-500">Lat: <span className="text-cyan-400">{formData.latitude.toFixed(5)}</span></span>
                  <span className="text-slate-500">Lng: <span className="text-cyan-400">{formData.longitude.toFixed(5)}</span></span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 py-2">
                <div className="">

                  <div className="mt-5 flex flex-col gap-3">

                    {/* ============================================ */}
                    {/* INPUT GPS GABUNGAN - FORMAT: lat, lng */}
                    {/* ============================================ */}
                    <div className="relative">
                      <label
                        htmlFor="gps-input"
                        className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5"
                      >
                        GPS
                      </label>
                      <div className="relative">
                        <input
                          id="gps-input"
                          type="text"
                          inputMode="decimal"
                          placeholder="-6.17511, 106.827153"
                          value={gpsInput}
                          onChange={handleGpsInputChange}
                          className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all font-mono pr-20"
                          required
                        />
                        <button
                          type="button"
                          onClick={safeDetectGps}
                          disabled={loadingGps}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-50"
                          title="Deteksi GPS"
                        >
                          {loadingGps ? <FiLoader className="animate-spin text-sm" /> : <FiCrosshair className="text-sm" />}
                        </button>
                      </div>
                      <p className="text-[10px] text-slate-600 mt-1">Format: latitude, longitude</p>
                    </div>

                    <TextAreaDefault
                      id="address"
                      label="Detail Alamat"
                      placeholder="Alamat akan terisi otomatis..."
                      value={formData.address}
                      onChange={handleChange}
                    />

                    {/* Pesan Error GPS (jika ada) */}
                    {gpsError && (
                      <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <span className="text-amber-400 text-[11px] leading-relaxed">
                          ⚠️ {gpsError} — Menggunakan lokasi default. Klik peta atau ikon crosshair untuk mengubah.
                        </span>
                      </div>
                    )}

                    <p className="text-[10px] text-slate-500 mt-1">* Klik peta untuk mengisi otomatis.</p>
                  </div>
                </div>

                <div className="min-h-[400px] w-full relative z-0 rounded-xl overflow-clip">
                  <MapContainer
                    center={[formData.latitude, formData.longitude]}
                    zoom={15}
                    scrollWheelZoom={true}
                    className="w-full h-full"
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapController data={formData} setData={setFormData} />
                  </MapContainer>
                </div>
              </div>
            </div>

            {/* BUTTONS */}
            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-300 border border-slate-600 hover:bg-slate-800 transition-all uppercase tracking-widest"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-xl font-bold text-sm uppercase tracking-widest shadow-lg shadow-cyan-900/20 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <FiLoader className="animate-spin" />
                    Menyimpan...
                  </>
                ) : (
                  "Simpan Data"
                )}
              </button>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};